<template>
  <div>
    <b-card>
      <b-row>
        <b-col
          v-if="hasPermission('privacy-gap', 'create')"
          md="3"
          class="mb-1"
        >
          <b-button
            variant="outline-primary"
            :to="{ name: 'privacy-gap-add' }"
          >
            <feather-icon
              icon="PlusIcon"
              class="mr-50"
            />
            {{ $t('Agregar Evaluación') }}
          </b-button>
        </b-col>

        <b-col
          v-if="hasPermission('privacy-gap', 'export')"
          md="2"
          class="mb-1"
        >
          <b-button
            variant="outline-success"
            :disabled="!gaps.length"
            @click="handleExportExcel"
          >
            <feather-icon
              icon="DownloadIcon"
              class="mr-50"
            />
            {{ $t('Export Excel') }}
          </b-button>
        </b-col>

        <b-col md="2" class="mb-1">
          <b-button
            variant="outline-secondary"
            :disabled="clearFiltersDisabled"
            @click="clearFilters"
          >
            <feather-icon
              icon="TrashIcon"
              class="mr-50"
            />
            {{ $t('clear_filters') }}
          </b-button>
        </b-col>

        <b-col md="5">
          <b-form-group>
            <b-form-input
              v-model="searchQuery"
              :placeholder="$t('message.search')"
              type="search"
              class="d-inline-block"
            />
          </b-form-group>
        </b-col>
      </b-row>

      <b-row v-if="hasPermission('privacy-gap', 'edit')" class="mb-2">
        <b-col cols="12">
          <b-button
            variant="outline-info"
            size="sm"
            @click="showObligationManager = true"
          >
            <feather-icon icon="SettingsIcon" class="mr-50" />
            Gestionar Catálogo de Obligaciones
          </b-button>
        </b-col>
      </b-row>

      <b-row>
        <b-col>
          <filters
            :gapStatusFilterShow="true"
            :workerFilterShow="true"
            :workerPlaceholderVariable="'Responsable'"
            :obligationFilterShow="true"
            :overdueGapsFilterShow="true"
            @changeFilter="updateFilter"
          />
        </b-col>
      </b-row>

      <b-row v-if="gaps.length > 0" class="mb-2">
        <b-col cols="12">
          <b-card class="mb-0">
            <div class="d-flex align-items-center justify-content-center">
              <div>
                <h5 class="mb-0">
                  <feather-icon icon="CheckCircleIcon" size="20" class="mr-50" />
                  Progreso de Cumplimiento
                </h5>
                <small class="text-muted">
                  Ley 21.719 - {{ totalGaps }} obligacion{{ totalGaps !== 1 ? 'es' : '' }} evaluada{{ totalGaps !== 1 ? 's' : '' }}
                </small>
              </div>
              <div class="text-right ml-2">
                <h2 class="mb-0" :class="complianceProgressClass">
                  {{ complianceProgress }}%
                </h2>
                <b-progress
                  :value="complianceProgress"
                  :variant="complianceProgressVariant"
                  height="8px"
                  class="mt-1"
                  style="width: 200px;"
                />
              </div>
            </div>
          </b-card>
        </b-col>
      </b-row>

      <b-row>
        <b-col cols="12">
          <b-table
            ref="refGapTable"
            :items="gaps"
            hover
            responsive
            :fields="tableColumns"
            primary-key="_id"
            :sort-by.sync="sortBy"
            show-empty=""
            :empty-text="isLoading ? $t('message.loading') : $t('message.no_records')"
            :sort-desc.sync="isSortDirDesc"
            @row-clicked="handleRowClick"
            tbody-tr-class="cursor-pointer"
          >
            <!-- N° -->
            <template #cell(obligationOrder)="data">
              <span class="font-weight-bold">{{ data.item.obligationOrder }}</span>
            </template>

            <!-- Obligación -->
            <template #cell(obligationName)="data">
              <span>{{ data.item.obligationName }}</span>
            </template>

            <!-- Artículo -->
            <template #cell(obligationArticle)="data">
              <span class="text-muted">{{ data.item.obligationArticle }}</span>
            </template>

            <!-- Estado -->
            <template #cell(status)="data">
              <gap-status-badge :status="data.item.status || 'No implementado'" />
            </template>

            <!-- Responsable -->
            <template #cell(responsibleName)="data">
              <span>{{ data.item.responsibleName || 'Sin asignar' }}</span>
            </template>

            <!-- Plazo -->
            <template #cell(deadline)="data">
              <span v-if="data.item.deadline" :class="getDeadlineClass(data.item.deadline)">
                {{ formatDate(data.item.deadline) }}
              </span>
              <span v-else class="text-muted">-</span>
            </template>

            <!-- Estado Plan -->
            <template #cell(planStatus)="data">
              <b-badge
                v-if="data.item.status === 'Parcial' || data.item.status === 'No implementado'"
                :variant="getPlanStatusVariant(data.item.planStatus)"
                class="font-weight-bold"
              >
                {{ data.item.planStatus || 'Pendiente' }}
              </b-badge>
              <span v-else class="text-muted">-</span>
            </template>

            <!-- Acciones -->
            <template #cell(actions)="data">
              <div class="flex flex-column">
                <EditButton
                  v-if="hasPermission('privacy-gap', 'edit')"
                  @clicked="router.push({name: 'privacy-gap-edit', params: { id: data.item._id }})"
                />
                <DeleteButton
                  v-if="hasPermission('privacy-gap', 'delete')"
                  v-b-modal="`delete modal ${data.item._id}`"
                />
                <b-modal
                  :id="`delete modal ${data.item._id}`"
                  centered
                  no-close-on-backdrop
                  modal-class="modal-danger"
                  ok-variant="danger"
                  cancel-variant="outline-secondary"
                  :title="$t('message.confirm_action')"
                  :ok-title="$t('message.delete')"
                  :cancel-title="$t('message.cancel')"
                  @ok="deleteGap(data.item)"
                >
                  <span>{{ $tc('¿Está seguro de eliminar esta evaluación de brecha?') }}</span>
                </b-modal>
              </div>
            </template>
          </b-table>
        </b-col>

        <b-col
          cols="12"
          class="d-flex justify-content-between flex-wrap"
        >
          <div class="mb-1">
            <b-form-group class="mb-0">
              <label class="d-inline-block text-sm-left mr-50">{{ $t('message.per_page') }}</label>
              <b-form-select
                id="perPageSelect"
                v-model="perPage"
                size="sm"
                :options="[10, 25, 50, 100]"
                class="per-page-selector"
              />
            </b-form-group>
          </div>

          <div class="mb-1">
            <span class="text-muted">{{ $tc('message.paginationText', 0, { from: `${((currentPage * perPage) - perPage) + (gaps.length ? 1 : 0)}`, to: `${gaps.length + ((currentPage * perPage) - perPage)}`, total: `${totalGaps}` }) }}</span>
          </div>

          <b-pagination
            v-model="currentPage"
            :total-rows="totalGaps"
            :per-page="perPage"
            first-number=""
            last-number=""
            prev-class="prev-item"
            next-class="next-item"
          />
        </b-col>
      </b-row>
    </b-card>

    <b-modal
      v-model="showObligationManager"
      title="Gestión de Obligaciones"
      size="xl"
      hide-footer
      no-close-on-backdrop
    >
      <obligation-manager @close="showObligationManager = false" @updated="fetchGaps" />
    </b-modal>
  </div>
</template>

<script>
import { BBadge, BPagination, BTable, BModal, VBModal, BProgress } from 'bootstrap-vue'
import { onMounted, computed, ref } from '@vue/composition-api'
import { useRouter } from '@core/utils/utils'
import { hasPermission } from '@/composables/usePermission'
import EditButton from '@/views/components/Shared/Buttons/EditButton.vue'
import DeleteButton from '@/views/components/Shared/Buttons/DeleteButton.vue'
import Filters from '@/views/organization/Filters.vue'
import GapStatusBadge from '../../components/GapStatusBadge.vue'
import ObligationManager from '../shared/ObligationManager.vue'
import useGapList from './useGapList'
import useExportGAP from './useExportGAP'
import Ripple from 'vue-ripple-directive'

export default {
  name: 'GapList',
  components: {
    BTable,
    BBadge,
    BPagination,
    EditButton,
    DeleteButton,
    Filters,
    BModal,
    GapStatusBadge,
    ObligationManager,
    BProgress,
  },
  directives: {
    'b-modal': VBModal,
    Ripple,
  },
  setup() {
    const { router } = useRouter()
    const showObligationManager = ref(false)

    const {
      fetchGaps,
      tableColumns,
      perPage,
      currentPage,
      totalGaps,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refGapTable,
      gaps,
      deleteGap,
      isLoading,
      clearFilters,
      clearFiltersDisabled,
      updateFilter,
      fetchAllGapsForExport,
      complianceProgress,
    } = useGapList()

    const { exportToExcel } = useExportGAP()

    const getPlanStatusVariant = (status) => {
      const variants = {
        'Pendiente': 'warning',
        'En progreso': 'info',
        'Cerrado': 'success',
      }
      return variants[status] || 'secondary'
    }

    const formatDate = (date) => {
      if (!date) return '-'
      const d = new Date(date)
      const year = d.getUTCFullYear()
      const month = String(d.getUTCMonth() + 1).padStart(2, '0')
      const day = String(d.getUTCDate()).padStart(2, '0')
      return `${day}-${month}-${year}`
    }

    const getDeadlineClass = (deadline) => {
      if (!deadline) return ''
      const today = new Date()
      const deadlineDate = new Date(deadline)
      const daysUntilDeadline = Math.floor((deadlineDate - today) / (1000 * 60 * 60 * 24))

      if (daysUntilDeadline < 0) return 'text-danger font-weight-bold'
      if (daysUntilDeadline <= 7) return 'text-warning font-weight-bold'
      return ''
    }

    const handleExportExcel = async () => {
      if (!hasPermission('privacy-gap', 'export')) return
      try {
        const allGaps = await fetchAllGapsForExport()
        exportToExcel(allGaps)
      } catch (error) {
        console.log(error)
      }
    }

    const handleRowClick = (item) => {
      router.push({ name: 'privacy-gap-edit', params: { id: item._id } })
    }

    const complianceProgressVariant = computed(() => {
      const progress = complianceProgress.value
      if (progress >= 75) return 'success'
      if (progress >= 50) return 'info'
      if (progress >= 25) return 'warning'
      return 'danger'
    })

    const complianceProgressClass = computed(() => {
      const progress = complianceProgress.value
      if (progress >= 75) return 'text-success'
      if (progress >= 50) return 'text-info'
      if (progress >= 25) return 'text-warning'
      return 'text-danger'
    })

    onMounted(() => {
      fetchGaps()
    })

    return {
      router,
      fetchGaps,
      tableColumns,
      perPage,
      currentPage,
      totalGaps,
      searchQuery,
      sortBy,
      isSortDirDesc,
      deleteGap,
      refGapTable,
      gaps,
      isLoading,
      clearFilters,
      clearFiltersDisabled,
      hasPermission,
      updateFilter,
      handleExportExcel,
      handleRowClick,
      getPlanStatusVariant,
      formatDate,
      getDeadlineClass,
      complianceProgress,
      complianceProgressVariant,
      complianceProgressClass,
      showObligationManager,
    }
  },
}
</script>

<style lang="scss" scoped>
.form-group {
  label {
    font-size: 0.85rem;
    font-weight: 400;
  }
}

.per-page-selector {
  width: 90px;
}

::v-deep .cursor-pointer {
  cursor: pointer;
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
